/**
 * Host (croupier) config panel + Start. Decks 1–8, turn timer 5–30s. Sends
 * `setConfig` on change and `start` on the button. Only the croupier sees this;
 * the server enforces role + ranges (intents from non-hosts are dropped).
 */

import { useEffect, useState } from 'react';
import {
  MIN_DECKS,
  MAX_DECKS,
  MIN_TURN_TIMER_SECS,
  MAX_TURN_TIMER_SECS,
} from '@blackjack/shared';
import { useGameStore } from '../state/store';
import { useSend } from '../net/RealtimeProvider';

export function ConfigPanel() {
  const send = useSend();
  const config = useGameStore((s) => s.server.config);
  const roster = useGameStore((s) => s.server.roster);
  const selfId = useGameStore((s) => s.serverAssignedId ?? s.identity?.clientId);
  const identity = useGameStore((s) => s.identity);

  const self = roster.find((r) => r.id === selfId);
  const isCroupier = (self?.role ?? identity?.role) === 'croupier';

  const [decks, setDecks] = useState(config.decks);
  const [turnTimerSecs, setTurnTimerSecs] = useState(config.turnTimerSecs);
  const [tieArmed, setTieArmed] = useState(false);

  // Keep local UI in sync if the server echoes a config change.
  useEffect(() => {
    setDecks(config.decks);
    setTurnTimerSecs(config.turnTimerSecs);
  }, [config.decks, config.turnTimerSecs]);

  if (!isCroupier) {
    return (
      <div className="panel config config--readonly">
        <h3 className="panel__title heading-gold">Table Rules</h3>
        <dl className="config__rules">
          <dt>Decks</dt>
          <dd>{config.decks}</dd>
          <dt>Turn timer</dt>
          <dd>{config.turnTimerSecs}s</dd>
          <dt>Dealer</dt>
          <dd>
            {config.dealerRule}
            <small>{config.dealerRule === 'S17' ? 'stands on soft 17' : 'hits soft 17'}</small>
          </dd>
        </dl>
      </div>
    );
  }

  const pushConfig = (d: number, t: number) => {
    send({ type: 'setConfig', decks: d, turnTimerSecs: t });
  };

  const players = roster.filter((r) => r.role === 'player');
  const canStart = players.length >= 1;

  return (
    <div className="panel config">
      <h3 className="panel__title heading-gold">Host Controls</h3>

      <label className="config__field">
        <span>Decks: {decks}</span>
        <input
          type="range"
          min={MIN_DECKS}
          max={MAX_DECKS}
          step={1}
          value={decks}
          onChange={(e) => {
            const d = Number(e.target.value);
            setDecks(d);
            pushConfig(d, turnTimerSecs);
          }}
        />
      </label>

      <label className="config__field">
        <span>Turn timer: {turnTimerSecs}s</span>
        <input
          type="range"
          min={MIN_TURN_TIMER_SECS}
          max={MAX_TURN_TIMER_SECS}
          step={1}
          value={turnTimerSecs}
          onChange={(e) => {
            const t = Number(e.target.value);
            setTurnTimerSecs(t);
            pushConfig(decks, t);
          }}
        />
      </label>

      <button
        type="button"
        className="btn-gold config__start"
        disabled={!canStart}
        onClick={() => {
          send({ type: 'start' });
          setTieArmed(false); // the armed tie (if any) fires this round
        }}
      >
        Start Round
      </button>
      {!canStart && <p className="config__warn">Need at least one player to start.</p>}

      {/* DEV ONLY: arm a rigged tie for the next round (≥2 betting players → a
          winner tie; ≥4 → winner + worst-loser ties) so the dice tiebreak can be
          reproduced. Hidden in production builds. */}
      {import.meta.env.DEV && (
        <button
          type="button"
          className="btn-action config__debug"
          onClick={() => {
            send({ type: 'debugForceTie' });
            setTieArmed(true);
          }}
        >
          {tieArmed ? '🎲 Tie armed ✓ — now Start' : '🎲 Force tie next round (dev)'}
        </button>
      )}
    </div>
  );
}
